import { roundMoney } from '../../utils/money';
import { formatProductDimensions } from '../../utils/productDimensions';

/**
 * Shared goods table of the transport freight cards — ZD on an INBOUND truck
 * (supplierOrderFreight.ts) and ZK on an OUTBOUND one (customerOrderFreight.ts).
 * Presentation of backend facts only: each line's cost is a backend figure
 * (ZD `loadItems[].freightPLN`, ZK `cargoItems[].freightPLN`) and the order cost
 * is the backend share; this module only divides a known cost by a positive
 * quantity. Nothing is re-split here.
 *
 * A unit a line has no stored equivalent for (a window has no m³) is not
 * offered for that line — unknown is never zero — and a total in a unit exists
 * only when every line can be expressed in it.
 */
export type LoadUnit = 'm2' | 'm3' | 'pcs';

export interface FreightLineUnitRow {
  unit: LoadUnit;
  quantity: number;
  /** Transport cost per one unit, canonical PLN; `null` when the line cost is unknown. */
  costPerUnit: number | null;
}

export interface FreightLineRow {
  id: string;
  name: string;
  /** e.g. "gr. 12,5 x szer. 1200 x dł. 2400 mm"; `null` when the product has none. */
  dimensions: string | null;
  /** This line's part of the order's transport cost (PLN); `null` = unknown. */
  costPLN: number | null;
  units: FreightLineUnitRow[];
}

export interface FreightTotalsUnit {
  /** Total quantity in this unit; `null` unless EVERY line has it. */
  quantity: number | null;
  /** Order cost / total quantity, canonical PLN; `null` when either is unknown. */
  average: number | null;
}

export interface FreightLinesTotals {
  /** The order's transport cost (PLN); `null` when unknown. */
  costPLN: number | null;
  pcs: FreightTotalsUnit;
  m2: FreightTotalsUnit;
  m3: FreightTotalsUnit;
}

/** Physical line as both loaders deliver it (numbers already normalized by the DTO). */
export interface FreightLineInput {
  id: string;
  name: string;
  width?: number | null;
  height?: number | null;
  length?: number | null;
  thickness?: number | null;
  dimensionDisplayProfile?: string | null;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  /** Backend line cost; `undefined` without transport-cost access. */
  freightPLN?: number | null;
}

type QuantityField = 'quantityPcs' | 'quantityM2' | 'quantityM3';

export const FREIGHT_UNIT_FIELDS: Array<{ unit: LoadUnit; field: QuantityField }> = [
  { unit: 'm2', field: 'quantityM2' },
  { unit: 'm3', field: 'quantityM3' },
  { unit: 'pcs', field: 'quantityPcs' },
];

export function positiveQuantity(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

export function buildFreightLine(item: FreightLineInput): FreightLineRow {
  const costPLN = item.freightPLN === undefined ? null : item.freightPLN;
  return {
    id: item.id,
    name: item.name,
    dimensions: formatProductDimensions({
      width: item.width ?? undefined,
      height: item.height ?? undefined,
      length: item.length ?? undefined,
      thickness: item.thickness ?? undefined,
      dimensionDisplayProfile: item.dimensionDisplayProfile,
    }),
    costPLN,
    units: FREIGHT_UNIT_FIELDS.flatMap(({ unit, field }): FreightLineUnitRow[] => {
      const quantity = positiveQuantity(item[field]);
      return quantity === null ? [] : [{
        unit,
        quantity,
        costPerUnit: costPLN === null ? null : roundMoney(costPLN / quantity),
      }];
    }),
  };
}

/** "Razem / Średnio" for the given order cost; `null` when there are no lines. */
export function buildFreightTotals(items: FreightLineInput[], costPLN: number | null): FreightLinesTotals | null {
  if (items.length === 0) return null;
  const unit = (field: QuantityField): FreightTotalsUnit => {
    const quantity = items.every((item) => positiveQuantity(item[field]) !== null)
      ? items.reduce((sum, item) => sum + (item[field] as number), 0)
      : null;
    return {
      quantity,
      average: costPLN === null || quantity === null ? null : roundMoney(costPLN / quantity),
    };
  };
  return { costPLN, pcs: unit('quantityPcs'), m2: unit('quantityM2'), m3: unit('quantityM3') };
}

/** Sum of the lines' backend costs; `null` when any is unknown. */
export function sumFreightLineCosts(items: FreightLineInput[]): number | null {
  return items.every((item) => item.freightPLN !== undefined && item.freightPLN !== null)
    ? roundMoney(items.reduce((sum, item) => sum + (item.freightPLN as number), 0))
    : null;
}
